<script setup>
import { ref, onMounted } from 'vue'

import { DxList } from 'devextreme-vue/list'

import { useODataSource } from '@/composables/data-grid'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'
import { useHighlightNewRow } from '@/composables/scroll'

import { planningApi } from '@/apis/planning.api'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useEquipmentQrScan } from '@/composables/qrcode'
import { useDetectDevice } from '@/composables/device'
import QrcodeScanner from '@/components/shared/QrcodeScanner.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import EquipmentDetail from '@/components/shared/EquipmentDetail.vue'
import EquipmentEdit from '@/components/shared/EquipmentEdit.vue'
import EquipmentListItem from '@/components/shared/EquipmentListItem.vue'
import SignalBar from '@/components/shared/signal-bar/SignalBar.vue'

import { useRoute } from 'vue-router'

const route = useRoute()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const isEmptyDataSource = ref(false)
const selectedItem = ref(null)
const editItem = ref(null)
const listContainer = ref(null)
let listInstance = null

const { isScanning, scanUuidAsync, onQrcodeDetected, onQrcodeScanError } = useEquipmentQrScan(
  (equipment) => { selectedItem.value = equipment }
)

const quickCreate = ref(false)
const onAddClick = (quick = false) => { quickCreate.value = quick === true; editItem.value = {} }

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-plus',
    onClick: () => onAddClick(false),
  },
  {
    id: 'quick-create',
    icon: 'mdi-flash',
    onClick: () => onAddClick(true),
  },
  {
    id: 'scan',
    icon: 'mdi-qrcode-scan',
    onClick: () => {
      isScanning.value = true
    },
  }
]

const dataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.equipmentsUrl, {
  onChanged: () => {
    isEmptyDataSource.value = dataSource.totalCount() === 0
  },
  searchExpr: 'search_field',
  filter: [['structure_type', '=', 1], 'or', ['structure_type', '=', 2]],
})

onMounted(async () => {
  if (route.query.uuid && route.query.uuid.length > 0) {
    await scanUuidAsync(route.query.uuid)
  }
})

const onDetailUpdate = () => {
  selectedItem.value = null
  dataSource.pageIndex(0)
  dataSource.reload()
}

const getRandomNumber = () => (Math.floor(Math.random() * 100) % 5) + 1

const onEditItemCreated = async () => {
  editItem.value = null
  webSiteStore.activateUpdatedDateSort(route.name)
  dataSource.sort(webSiteStore.buildSortOrderBy(route.name))
  dataSource.pageIndex(0)
  const loaded = await dataSource.reload()
  await highlightRowAsync(loaded?.[0]?.id, listContainer)
  listInstance?.repaint()
}

const onEditDismissed = () => {
  editItem.value = null
  dataSource.reload()
}


useEntityChanges([entityChangeTypeEnum.EQUIPMENT], (change) => (change.created ? onEditItemCreated() : dataSource.reload()), () => editItem.value !== null)
</script>
<template>
  <div>
    <filter-sort-selector
      :data-source="dataSource"
      :kpi-enabled="true"
      :kpi-visible="webSiteStore.isKpiBarVisible('equipments')"
      @toggle-kpi="webSiteStore.toggleKpiBar('equipments')"
    />
    <signal-bar page="equipments" embedded :visible="webSiteStore.isKpiBarVisible('equipments')" />
    <q-card flat>
      <q-card-section ref="listContainer" class="q-pa-none">
        <q-dialog
          maximized
          :model-value="selectedItem !== null"
        >
          <equipment-detail
            :model-value="selectedItem"
            @cancel="onDetailUpdate"
            @update="onDetailUpdate"
          />
        </q-dialog>
        <q-dialog
          :maximized="isMobile"
          :model-value="editItem !== null"
          class="dialog-sm"
        >
          <equipment-edit
            v-if="editItem !== null"
            :model-value="editItem"
            :initial-quick-create="quickCreate"
            @update="onEditItemCreated"
            @cancel="onEditDismissed"
            @delete="onEditDismissed"
          />
        </q-dialog>
        <q-dialog
          maximized
          :model-value="isScanning"
        >
          <qrcode-scanner
            v-if="isScanning"
            :timeout="5000"
            @detect="onQrcodeDetected"
            @timeout="onQrcodeScanError"
            @error="onQrcodeScanError"
          />
        </q-dialog>
        <dx-list
          v-show="!isEmptyDataSource"
          :data-source="dataSource"
          height="calc(100vh - 8rem)"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: availableItem }">
            <q-item>
              <equipment-list-item
                :key="availableItem.id"
                :equipment="availableItem"
                class="q-pa-sm"
                :class="{ 'row-highlight': availableItem.id === highlightedRowId }"
                dense
                clickable
                @click="selectedItem = availableItem"
              />
            </q-item>
          </template>
        </dx-list>
        <div
          v-show="isEmptyDataSource"
          class="empty-container"
        >
          <img
            src="/img/no-equipments.svg"
            :style="{ maxWidth: '80vw', objectFit: 'contain' }"
            class="q-mb-xl"
          >
          <p class="text-h5">
            {{ $t('common.noEquipment') }}
          </p>
          <p class="sub text-weight-light">
            {{ $t(`common.noEquipment${getRandomNumber()}`) }}
          </p>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>
